/**
 * /well-servicing/tours/:id — one tour sheet, office side (GET / PATCH /
 * DELETE /well-servicing/tours/:id, submit / approve / return,
 * fieldiq-api#1282, WS-2).
 *
 * A full page. The sheet is edited in place while it is a draft (the tour
 * roles: admin, office staff, supervisor, field hand); once submitted it is
 * read only. The office (admin, office staff) approves a submitted sheet, or
 * returns it to draft with a reason the hand sees, or deletes a draft. A
 * returned sheet shows that reason at the top.
 *
 * SUBMIT SENDS THE SAVED SHEET. The API checks the saved activities cover the
 * sheet end to end and that there is crew, and names each gap if not; the
 * refusal is shown as that sentence. Hours, gaps and totals are the API's.
 *
 * WS-3: below the sheet, its links to the shared pressure-control tests and
 * drills (TourLinks); the String and Tallies are on the job's tabs.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, ClipboardList, Clock, Send, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { canUseWsAi } from '../../lib/ws-ai';
import { WsAiLauncher } from './ai/WsAiBits';
import { useWsActivityCodes, useWsMeta, useWsRig, useWsUsers } from '../../hooks/useWellServicing';
import {
  useApproveWsTour,
  useDeleteWsTour,
  useReturnWsTour,
  useSaveWsTour,
  useSubmitWsTour,
  useWsJob,
  useWsTour,
} from '../../hooks/useWsJobs';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canApproveWsTours, canWriteWsTours, wsDay, wsHours, wsJobRefusal, wsTourStatusSkin } from '../../lib/well-servicing';
import type { WsMeterWarning } from '../../types/well-servicing';
import { useBackToList } from '../downhole/recordPage';
import { TourEditor, TourMeterWarnings, TourReadout, TourSummary, tourCatalog, tourDefaults } from './TourEditor';
import { officeTourValues, tourCrewOptions, tourUnitOptions } from './tourValues';
import { TourLinksPanel } from './TourLinks';
import { canReadWsTickets } from '../../lib/ws-tickets';
import { TourTicketLink } from './tickets/TourTicketLink';
import {
  isNotFound,
  WS_BAND_SECONDARY,
  WS_EYEBROW,
  WS_INPUT,
  WS_PRIMARY,
  WS_SECONDARY,
  WsBadge,
  WsDialog,
  WsEmpty,
  WsField,
  WsFormError,
  WsLoading,
  WsPanel,
} from './WsBits';

type Action = 'submit' | 'approve' | 'return' | 'delete';

export default function WsTourScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const canWrite = canWriteWsTours(role);
  const canApprove = canApproveWsTours(role);
  const query = useWsTour(id);
  const tour = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const back = useBackToList('/well-servicing/jobs');
  const jobQuery = useWsJob(tour?.jobId);
  const job = jobQuery.data?.data ?? null;
  const rig = useWsRig(job?.rigId ?? undefined);
  const codes = useWsActivityCodes('true');
  const catalog = useWsMeta().data?.data ?? null;
  const users = useWsUsers(canWrite && role !== 'field_worker');
  const save = useSaveWsTour();
  const submit = useSubmitWsTour();
  const approve = useApproveWsTour();
  const ret = useReturnWsTour();
  const del = useDeleteWsTour();
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<WsMeterWarning[]>([]);
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const values = tour ? officeTourValues(tour) : null;
  const draft = tour?.status === 'draft';
  const editable = !!tour && draft && !tour.locked && canWrite;
  const units = values ? tourUnitOptions(job, values.workDate, values.equipment) : [];
  const crewOptions = values ? tourCrewOptions(rig.data?.data ?? null, values.crew, users.data ?? []) : [];
  const cat = tourCatalog(catalog);
  const defaults = tourDefaults(meta?.unitSystem, meta?.units?.depth);
  const unitName = (equipmentId: string | null) => units.find((u) => u.equipmentId === equipmentId)?.name ?? null;
  const busy = submit.isPending || approve.isPending || ret.isPending || del.isPending;
  const backToJob = () => (tour ? navigate(`/well-servicing/jobs/${tour.jobId}?tab=tours`) : back());

  const run = () => {
    if (!tour || !action) return;
    setActionError(null);
    const done = (msg: string) => () => {
      toast.success(msg);
      setAction(null);
      setReason('');
    };
    const fail = (fallback: string) => (err: unknown) => setActionError(wsJobRefusal(err, fallback));
    if (action === 'submit') submit.mutate(tour.id, { onSuccess: done('Tour sheet submitted'), onError: fail('The sheet could not be submitted.') });
    if (action === 'approve') approve.mutate(tour.id, { onSuccess: done('Tour sheet approved'), onError: fail('The sheet could not be approved.') });
    if (action === 'return') {
      if (!reason.trim()) return setActionError('Say what the crew should fix.');
      ret.mutate({ id: tour.id, reason: reason.trim() }, { onSuccess: done('Tour sheet returned to draft'), onError: fail('The sheet could not be returned.') });
    }
    if (action === 'delete')
      del.mutate(tour.id, {
        onSuccess: () => {
          toast.success('Tour sheet deleted');
          navigate(`/well-servicing/jobs/${tour.jobId}?tab=tours`, { replace: true });
        },
        onError: fail('The sheet could not be deleted.'),
      });
  };

  const TITLES: Record<Action, string> = { submit: 'Submit tour sheet', approve: 'Approve tour sheet', return: 'Return to draft', delete: 'Delete tour sheet' };
  const BODIES: Record<Action, string> = {
    submit: 'Submits the saved sheet to the office. Save any changes first. The activities must cover the sheet end to end, and it needs crew.',
    approve: 'Approves the sheet. An approved sheet is locked.',
    return: 'Sends the sheet back to draft so the crew can fix it. They will see your reason.',
    delete: 'Deletes this draft sheet. The rig can then have a new sheet for the day.',
  };

  return (
    <div data-testid="ws-tour-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title={tour ? `Tour sheet · ${wsDay(tour.workDate)}` : query.isLoading ? 'Tour sheet' : '—'}
        description={tour ? `${tour.jobNumber ?? '—'} · ${tour.rigNumber ?? '—'} · ${tour.wellName ?? '—'}` : undefined}
        onBack={tour ? backToJob : back}
        backLabel="Back"
        actions={
          tour ? (
            <div className="flex flex-wrap gap-2" data-testid="ws-tour-actions">
              {draft && canWrite && (
                <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setActionError(null); setAction('submit'); }}>
                  <Send className="size-4" aria-hidden="true" />
                  Submit
                </button>
              )}
              {tour.status === 'submitted' && canApprove && (
                <>
                  <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={() => { setActionError(null); setAction('approve'); }}>
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    Approve
                  </button>
                  <button type="button" className={WS_BAND_SECONDARY} onClick={() => { setActionError(null); setReason(''); setAction('return'); }}>
                    <Undo2 className="size-4" aria-hidden="true" />
                    Return to draft
                  </button>
                </>
              )}
              {draft && canApprove && (
                <button type="button" className={WS_BAND_SECONDARY} onClick={() => { setActionError(null); setAction('delete'); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: tour ? wsTourStatusSkin(tour.status, tour.statusLabel).label : '—' },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Sheet', sub: tour ? `${tour.startTime}–${tour.endTime}` : undefined, tone: 'neutral', loading: query.isLoading, value: tour ? wsHours(tour.timeline?.tourHours) : '—' },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Covered', tone: tour?.timeline?.complete ? 'accent' : 'neutral', loading: query.isLoading, value: tour ? wsHours(tour.timeline?.coveredHours) : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WsLoading label="Loading tour sheet" />
          ) : notFound || (!failure && !tour) ? (
            <WsPanel eyebrow="Tour sheet" title="Tour sheet">
              <WsEmpty>This tour sheet was not found. It may have been deleted, or you may not be on the job.</WsEmpty>
            </WsPanel>
          ) : failure || !tour || !values ? (
            <WsPanel eyebrow="Tour sheet" title="Tour sheet">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this tour sheet" />
            </WsPanel>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <WsBadge skin={wsTourStatusSkin(tour.status, tour.statusLabel)} value={tour.status} />
                {tour.submittedAt && <span className="text-sm text-fieldiq-muted">Submitted by {tour.submittedByName || '—'}</span>}
                {tour.approvedAt && <span className="text-sm text-fieldiq-muted">Approved by {tour.approvedByName || '—'}</span>}
              </div>
              {draft && tour.returnedReason && (
                <p role="status" data-testid="ws-tour-returned" className="rounded-fi-control border border-fi-amber/60 bg-fi-amber/10 px-4 py-3 text-sm text-fieldiq-text">
                  <span className="font-bold">Returned by the office: </span>
                  {tour.returnedReason}
                </p>
              )}
              <TourMeterWarnings warnings={warnings.map((w) => ({ equipmentId: w.equipmentId, entered: w.entered, previous: w.previous, previousDate: w.previousDate ? wsDay(w.previousDate) : null }))} unitName={unitName} />
              <TourSummary values={values} billingClasses={catalog?.billingClasses} />
              {editable ? (
                codes.isLoading || jobQuery.isLoading ? (
                  <WsLoading label="Loading the sheet's options" />
                ) : (
                  <TourEditor
                    key={`${tour.id}-${tour.updatedAt ?? ''}`}
                    values={values}
                    codes={codes.data?.data ?? []}
                    crewOptions={crewOptions}
                    unitOptions={units}
                    catalog={cat}
                    defaults={defaults}
                    pending={save.isPending}
                    error={error}
                    submitLabel="Save sheet"
                    onSubmit={(body) => {
                      setError(null);
                      save.mutate(
                        { id: tour.id, body },
                        {
                          onSuccess: (res) => {
                            toast.success('Tour sheet saved');
                            setWarnings(res.warnings);
                          },
                          onError: (err) => setError(wsJobRefusal(err, 'The tour sheet could not be saved.')),
                        },
                      );
                    }}
                  />
                )
              ) : (
                <TourReadout values={values} catalog={cat} unitOptions={units} />
              )}
              <TourLinksPanel tour={tour} editable={editable} />
              {canReadWsTickets(role) && tour.status === 'office_approved' && <TourTicketLink tourId={tour.id} jobId={tour.jobId} workDate={tour.workDate} />}
              {canUseWsAi(role) && (
                <WsAiLauncher
                  request={{ agent: 'ws_tour_summary', tourId: tour.id }}
                  eyebrow="AI draft"
                  title="Tour summary"
                  blurb="Totals this sheet's hours, codes, crew, depth and string work exactly, then asks the AI to draft a summary. The draft waits for your approval; nothing is sent."
                  testId="ws-ai-tour-summary"
                />
              )}
            </>
          )}
        </div>
      </section>

      {tour && (
        <WsDialog
          open={!!action}
          title={action ? TITLES[action] : ''}
          onClose={() => setAction(null)}
          footer={
            <>
              <button type="button" className={WS_SECONDARY} onClick={() => setAction(null)} disabled={busy}>
                Not now
              </button>
              <button type="button" className={WS_PRIMARY} onClick={run} disabled={busy}>
                {busy ? 'Saving…' : action ? TITLES[action] : 'Confirm'}
              </button>
            </>
          }
        >
          <WsFormError message={actionError} />
          <p className="text-sm text-fieldiq-text">{action ? BODIES[action] : ''}</p>
          {action === 'return' && (
            <div className="mt-3">
              <WsField label="Reason">
                <textarea className={`${WS_INPUT} h-20 py-2`} aria-label="Return reason" value={reason} onChange={(e) => setReason(e.target.value)} />
              </WsField>
            </div>
          )}
        </WsDialog>
      )}
    </div>
  );
}
